/**
 * 平台领域模型 —— 前端、Worker、Mock 无人机服务器共用的唯一类型来源。
 */

/* ------------------------------------------------------------------ *
 * 权限
 * ------------------------------------------------------------------ */

/** 平台角色。权限矩阵见 roles.ts。 */
export type Role = 'admin' | 'operator' | 'maintainer' | 'viewer';

/** 全部权限位。以常量数组声明，便于运行时遍历与测试。 */
export const PERMISSIONS = [
  'fleet.view',
  'fleet.registry.view',
  'fleet.registry.write',
  'fleet.telemetry.view',
  'fleet.telemetry.stream',
  'drone.command.flight',
  'drone.command.mission',
  'drone.command.maintenance',
  'user.view',
  'user.manage',
  'audit.view',
  'settings.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/* ------------------------------------------------------------------ *
 * 用户
 * ------------------------------------------------------------------ */

export type UserStatus = 'active' | 'suspended';

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

/* ------------------------------------------------------------------ *
 * 无人机注册表
 * ------------------------------------------------------------------ */

export type DroneTransport = 'ws' | 'poll';

/** 无人机侧校验浏览器凭据的方式。 */
export type DroneCredentialMode = 'platformToken' | 'apiKey' | 'none';

export type DroneProtocol = 'http' | 'https';

/** 无人机服务器地址。域名 + 端口 + API 根路径。 */
export interface DroneEndpoint {
  protocol: DroneProtocol;
  host: string;
  port: number;
  /** REST 根路径，例如 `/api/v1` */
  basePath: string;
  /** WebSocket 路径，例如 `/api/v1/telemetry/stream` */
  wsPath: string;
}

/**
 * 告警阈值。字段名已自解释方向（Percent 为「下限」、Temp 为「上限」），
 * 具体判定方向在 alerts.ts 的指标定义中给出。
 */
export interface DroneAlertThresholds {
  /** 电量百分比下限（%） */
  batteryPercentCaution: number;
  batteryPercentWarning: number;
  batteryPercentCritical: number;
  /** 单体电压下限（V） */
  cellVoltageCaution: number;
  cellVoltageWarning: number;
  cellVoltageCritical: number;
  /** 可见卫星数下限 */
  satellitesCaution: number;
  satellitesWarning: number;
  satellitesCritical: number;
  /** HDOP 上限 */
  hdopCaution: number;
  hdopWarning: number;
  hdopCritical: number;
  /** 光流质量下限（0-100） */
  opticalFlowCaution: number;
  opticalFlowWarning: number;
  opticalFlowCritical: number;
  /** 链路质量下限（0-100） */
  linkQualityCaution: number;
  linkQualityWarning: number;
  linkQualityCritical: number;
  /** 链路延迟上限（ms） */
  latencyCaution: number;
  latencyWarning: number;
  latencyCritical: number;
  /** 横滚/俯仰角上限（度） */
  attitudeCaution: number;
  attitudeWarning: number;
  attitudeCritical: number;
  /** 电池温度上限（摄氏度） */
  batteryTempCaution: number;
  batteryTempWarning: number;
  batteryTempCritical: number;
}

/** 无人机注册记录。**永不包含明文凭据**。 */
export interface Drone {
  id: string;
  /** 显示名称 */
  name: string;
  /** 机身唯一 ID / 序列号 */
  uniqueId: string;
  model: string;
  vendor: string;
  endpoint: DroneEndpoint;
  credentialMode: DroneCredentialMode;
  /** 是否已配置凭据（凭据本体不外传） */
  hasCredential: boolean;
  /** 适配器配置档名 */
  adapterProfile: string;
  /** 原始报文 → 规范字段的映射覆盖 */
  fieldMap: Record<string, string>;
  tags: string[];
  groupId: string | null;
  enabled: boolean;
  /** 单机管理页的遥测频率 */
  telemetryRateHz: number;
  /** 超过该时长未收到帧即判定链路失联 */
  staleAfterMs: number;
  /** 覆盖平台默认阈值（部分覆盖即可） */
  thresholds: Partial<DroneAlertThresholds>;
  notes: string;
  createdAt: string;
  updatedAt: string;
  lastSeenAt: string | null;
}

export interface DroneGroup {
  id: string;
  name: string;
  description: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

/** 新建无人机时的输入 */
export type DroneDraft = Omit<
  Drone,
  'id' | 'hasCredential' | 'createdAt' | 'updatedAt' | 'lastSeenAt'
> & {
  /** 仅在创建或轮换时提交 */
  credential?: string;
};

/** 批量配置允许修改的字段集合。 */
export interface DroneBatchPatch {
  enabled?: boolean;
  groupId?: string | null;
  tags?: string[];
  tagMode?: 'replace' | 'add' | 'remove';
  telemetryRateHz?: number;
  staleAfterMs?: number;
  thresholds?: Partial<DroneAlertThresholds>;
  rotateCredential?: boolean;
}

/* ------------------------------------------------------------------ *
 * 遥测
 * ------------------------------------------------------------------ */

export type GpsFixType = 'none' | '2d' | '3d' | 'rtk-float' | 'rtk-fixed';

export type FlightMode =
  | 'manual'
  | 'stabilize'
  | 'loiter'
  | 'auto'
  | 'rtl'
  | 'land'
  | 'offboard';

export type MissionState =
  | 'idle'
  | 'uploading'
  | 'ready'
  | 'executing'
  | 'paused'
  | 'completed'
  | 'failed';

export interface Attitude {
  /** 横滚角（度，右为正） */
  rollDeg: number;
  /** 俯仰角（度，抬头为正） */
  pitchDeg: number;
  /** 航向角（度，正北为 0，顺时针） */
  yawDeg: number;
  rollRateDegS: number;
  pitchRateDegS: number;
  yawRateDegS: number;
}

export interface GeoPosition {
  lat: number;
  lng: number;
  /** 相对起飞点高度（米） */
  altM: number;
  /** 海拔（米） */
  altAbsM: number;
  /** 相对起飞点水平距离（米） */
  homeDistanceM: number;
}

export interface Velocity {
  /** 地速（米/秒） */
  groundSpeedMs: number;
  /** 垂直速度（米/秒，上升为正） */
  verticalSpeedMs: number;
  northMs: number;
  eastMs: number;
}

export interface BatteryState {
  /** 剩余电量百分比 0-100 */
  percent: number;
  /** 总电压（V） */
  voltageV: number;
  /** 电流（A，放电为正） */
  currentA: number;
  cellCount: number;
  /** 各单体电压（V） */
  cellVoltagesV: number[];
  temperatureC: number;
  /** 剩余可飞行时间估算（秒） */
  remainingSec: number;
}

export interface GpsState {
  fixType: GpsFixType;
  /** 可见卫星数 */
  satellites: number;
  hdop: number;
  vdop: number;
}

export interface OpticalFlowState {
  /** 光流质量 0-100 */
  quality: number;
  /** 光流是否参与定位 */
  enabled: boolean;
  /** 光流高度（米） */
  heightM: number;
}

export interface LinkState {
  /** 信号强度 dBm */
  rssiDbm: number;
  /** 综合信号质量 0-100 */
  quality: number;
  /** 往返延迟 ms */
  latencyMs: number;
  /** 丢包率 0-100 */
  packetLossPct: number;
  /** 由前端 Hub 标注的实际传输方式 */
  transport: DroneTransport;
}

export interface FlightState {
  armed: boolean;
  inAir: boolean;
  mode: FlightMode;
  missionState: MissionState;
  missionId: string | null;
  missionCurrentWaypoint: number;
  missionTotalWaypoints: number;
}

export interface GimbalState {
  pitchDeg: number;
  yawDeg: number;
  rollDeg: number;
}

export interface EnvironmentState {
  temperatureC: number;
  windSpeedMs: number;
  windDirectionDeg: number;
  pressureHpa: number;
}

/** 规范化的无人机遥测帧。所有时间均为 epoch 毫秒。 */
export interface DroneTelemetry {
  droneId: string;
  /** 无人机侧帧序号，用于丢帧检测 */
  seq: number;
  /** 无人机侧时间戳 */
  timestamp: number;
  /** 前端接收时间戳 */
  receivedAt: number;
  attitude: Attitude;
  position: GeoPosition;
  velocity: Velocity;
  battery: BatteryState;
  gps: GpsState;
  opticalFlow: OpticalFlowState;
  link: LinkState;
  flight: FlightState;
  gimbal: GimbalState;
  environment: EnvironmentState;
  /** 未能映射的原始字段，保留以便排查 */
  raw: Record<string, unknown>;
}

/** 飞行器静态信息。 */
export interface DroneInfo {
  droneId: string;
  uniqueId: string;
  name: string;
  model: string;
  vendor: string;
  firmwareVersion: string;
  /** 开机时长（秒） */
  uptimeSec: number;
  home: { lat: number; lng: number } | null;
}

export interface Waypoint {
  lat: number;
  lng: number;
  altM: number;
  speedMs: number;
  holdSec: number;
}

/* ------------------------------------------------------------------ *
 * 审计
 * ------------------------------------------------------------------ */

export type AuditResult = 'success' | 'denied' | 'failure';

export interface AuditLog {
  id: string;
  at: string;
  actorUserId: string | null;
  actorEmail: string;
  /** 形如 `drone.command.flight` / `user.manage.update` */
  action: string;
  targetType: 'drone' | 'user' | 'group' | 'settings' | 'auth' | 'system';
  targetId: string | null;
  detail: Record<string, unknown> | null;
  result: AuditResult;
  ip: string | null;
}

/* ------------------------------------------------------------------ *
 * 平台设置
 * ------------------------------------------------------------------ */

export interface PlatformSettings {
  platformName: string;
  /** 总览页遥测频率 */
  overviewTelemetryRateHz: number;
  /** 单机管理页遥测频率 */
  singleDroneTelemetryRateHz: number;
  defaultTelemetryRateHz: number;
  defaultStaleAfterMs: number;
  defaultThresholds: DroneAlertThresholds;
  auditRetentionDays: number;
  /** 是否允许 Worker 主动探测无人机端点（SSRF 风险，默认关闭） */
  allowDroneProbe: boolean;
}

/* ------------------------------------------------------------------ *
 * API 报文
 * ------------------------------------------------------------------ */

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface LoginResponse {
  user: User;
  permissions: Permission[];
  /**
   * 为 true 时，除改密与登出以外的接口都会返回 403 `password_change_required`。
   * 初始管理员使用内置默认口令时会置为 true。
   */
  mustChangePassword: boolean;
}

export type MeResponse = LoginResponse;

/** Worker 签发的短期无人机访问令牌。 */
export interface DroneAccessToken {
  token: string;
  /** epoch 毫秒 */
  expiresAt: number;
  droneId: string;
  mode: DroneCredentialMode;
}
